/**
 * PURE — value-level matching for the wizard's Accounts, Metrics and Categories
 * steps.
 *
 * Mirrors `services/datahub/core/qc_engine.py` (`account_name_variants`,
 * `resolve_account`, `top_matches`) so the wizard proposes exactly what the
 * server-side QC engine would conclude. If these two drift, the wizard will
 * promise a resolution the import then rejects.
 *
 * Decisions made here are applied by REWRITING the uploaded CSV, never by
 * persisting a mapping: the reference app forgets account decisions between
 * runs and ExecBridge now matches that deliberately.
 */

export function norm(s: string | null | undefined): string {
  return (s ?? "").trim().toLowerCase();
}

/**
 * A metric id as the server stores it: lowercase letters, digits and
 * underscores. Mirrors `services/kpi_catalog.py::_metric_id_slug`.
 */
export function metricIdSlug(raw: string | null | undefined): string {
  return (raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Trailing source-system decoration, e.g. "Wipro Limited. - Salesforce". */
const SOURCE_SUFFIX_RE =
  /\s*[-–—|/]\s*(salesforce|sfdc|hubspot|netsuite|zendesk|gainsight|crm|erp)\s*$/i;

/**
 * Progressively looser forms of a name, most exact first. Each remains an
 * *exact* lookup key — this widens what counts as exact, it is not fuzzy
 * matching.
 */
export function nameVariants(raw: string | null | undefined): string[] {
  const base = norm(raw);
  if (!base) return [];
  const out = [base];
  const add = (candidate: string) => {
    const c = candidate.replace(/\s+/g, " ").trim();
    if (c && out.indexOf(c) === -1) out.push(c);
  };
  const stripped = base.replace(SOURCE_SUFFIX_RE, "");
  add(stripped);
  add(stripped.replace(/[\s.,;:]+$/, ""));
  add(base.replace(/[\s.,;:]+$/, ""));
  return out;
}

const SEPARATOR_RE = /[/_\-|&+]+/g;

function tokens(s: string): string[] {
  return norm(s).replace(SEPARATOR_RE, " ").split(/\s+/).filter(Boolean);
}

export type Candidate = { id: string; name: string };

export type Suggestion = Candidate & { score: number };

/**
 * Fuzzy suggestions, scored as the TS/Python do: exact 100, prefix 80,
 * substring 60, otherwise token overlap. Advisory only — never auto-applied.
 */
export function topMatches(
  query: string,
  rows: Candidate[],
  limit = 3
): Suggestion[] {
  const q = norm(query).replace(SEPARATOR_RE, " ").replace(/\s+/g, " ").trim();
  if (!q) return [];
  const qTokens = tokens(query);

  const scored: Suggestion[] = [];
  rows.forEach(r => {
    const n = norm(r.name)
      .replace(SEPARATOR_RE, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!n) return;
    let score: number;
    if (n === q) score = 100;
    else if (n.startsWith(q) || q.startsWith(n)) score = 80;
    else if (n.includes(q) || q.includes(n)) score = 60;
    else {
      const nTokens = tokens(r.name);
      const overlap = qTokens.filter(t => nTokens.indexOf(t) !== -1).length;
      if (!overlap) return;
      score = Math.round(
        (overlap / Math.max(qTokens.length, nTokens.length)) * 50
      );
    }
    scored.push({ ...r, score });
  });

  return scored.sort((a, b) => b.score - a.score).slice(0, limit);
}

export type MatchStatus = "resolved" | "ambiguous" | "unresolved";

export type MatchProposal = {
  /** The value as it appears in the file. */
  source: string;
  status: MatchStatus;
  /** Chosen target id, or null when the user must decide. */
  chosenId: string | null;
  /** How it resolved, for the run summary. */
  how: string | null;
  suggestions: Suggestion[];
  /** Rows in the file carrying this value. */
  rowCount: number;
  /**
   * Plain-language reasons shown in place of the suggestions, e.g. for a
   * category whose name and file id disagree: "Name says: …", "Category ID
   * says: …". Absent for ordinary proposals.
   */
  detail?: string[];
};

/**
 * Propose a target for one source value.
 *
 * An exact or uniquely-loosened match resolves; anything else is left for a
 * human. The uniqueness rule is the safety rail — silently attaching one
 * customer's metrics to another is worse than asking.
 */
export function proposeMatch(
  source: string,
  candidates: Candidate[],
  rowCount: number,
  /**
   * When set, a fuzzy match at or above this score auto-resolves, provided its
   * score is strictly better than the runner-up. Used for CATEGORY, where the
   * file's short labels ("Support") need to meet the rubric's long ones
   * ("Support & Risk Signals") and a wrong guess only mislabels — it cannot
   * attach data to the wrong customer. Left off for accounts and metrics,
   * where identity matters and a guess is dangerous.
   */
  fuzzyThreshold?: number,
  /**
   * Also match on the cleaned id (see `metricIdSlug`), when exactly one
   * candidate shares it. For METRICS only: a custom metric created from
   * "count_ticket_solved_(hwsw)" is stored as `count_ticket_solved_hwsw`, and
   * the next import of the same file must still find it without a human.
   */
  options: { matchIdSlug?: boolean } = {}
): MatchProposal {
  const base: Omit<MatchProposal, "status" | "chosenId" | "how"> = {
    source,
    suggestions: topMatches(source, candidates),
    rowCount,
  };

  const byId = new Map(candidates.map(c => [norm(c.id), c]));
  const byName = new Map(candidates.map(c => [norm(c.name), c]));

  const direct = byId.get(norm(source)) ?? byName.get(norm(source));
  if (direct) {
    return { ...base, status: "resolved", chosenId: direct.id, how: "exact" };
  }

  if (options.matchIdSlug) {
    const key = metricIdSlug(source);
    const same = key ? candidates.filter(c => metricIdSlug(c.id) === key) : [];
    if (same.length === 1) {
      return {
        ...base,
        status: "resolved",
        chosenId: same[0].id,
        how: "normalized",
      };
    }
    if (same.length > 1) {
      return { ...base, status: "ambiguous", chosenId: null, how: null };
    }
  }

  for (const variant of nameVariants(source)) {
    const hit = byName.get(variant);
    if (hit) {
      return {
        ...base,
        status: "resolved",
        chosenId: hit.id,
        how: "normalized",
      };
    }
    const loose = candidates.filter(
      c => nameVariants(c.name).indexOf(variant) !== -1
    );
    if (loose.length === 1) {
      return {
        ...base,
        status: "resolved",
        chosenId: loose[0].id,
        how: "normalized",
      };
    }
    if (loose.length > 1) {
      return { ...base, status: "ambiguous", chosenId: null, how: null };
    }
  }

  if (fuzzyThreshold != null && base.suggestions.length > 0) {
    const [best, runnerUp] = base.suggestions;
    const decisive = !runnerUp || best.score > runnerUp.score;
    if (best.score >= fuzzyThreshold && decisive) {
      return { ...base, status: "resolved", chosenId: best.id, how: "similar" };
    }
  }

  return { ...base, status: "unresolved", chosenId: null, how: null };
}

/**
 * Resolve a still-open proposal through a table of alternative ids, e.g. the
 * reference app's `strategic_value_score` for ExecBridge's `strategic_value`.
 * Exact lookups only; a proposal that already resolved is left alone.
 */
export function resolveViaAliases(
  p: MatchProposal,
  aliasToId: Record<string, string>
): MatchProposal {
  if (p.status === "resolved") return p;
  const hit = aliasToId[metricIdSlug(p.source)];
  return hit ? { ...p, status: "resolved", chosenId: hit, how: "alias" } : p;
}

/** Distinct non-blank values of `column`, with how many rows carry each. */
export function distinctValues(
  rows: Record<string, string>[],
  column: string | null
): { value: string; rowCount: number }[] {
  if (!column) return [];
  const counts = new Map<string, number>();
  rows.forEach(r => {
    const v = (r[column] ?? "").trim();
    if (!v) return;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([value, rowCount]) => ({ value, rowCount }))
    .sort((a, b) => b.rowCount - a.rowCount);
}

export type DecisionCounts = {
  auto_matched: number;
  user_matched: number;
  skipped: number;
};

/**
 * `decisions` maps a source value to the target the user chose, or "skip".
 * A value absent from `decisions` keeps whatever the proposal concluded.
 */
export function countDecisions(
  proposals: MatchProposal[],
  decisions: Record<string, string>
): DecisionCounts {
  let auto_matched = 0;
  let user_matched = 0;
  let skipped = 0;
  proposals.forEach(p => {
    const chosen = decisions[p.source];
    if (chosen === "skip") skipped++;
    else if (chosen) user_matched++;
    else if (p.chosenId) auto_matched++;
    else skipped++;
  });
  return { auto_matched, user_matched, skipped };
}

/** The effective target for a source value, or null when it will be skipped. */
export function effectiveTarget(
  p: MatchProposal,
  decisions: Record<string, string>
): string | null {
  const chosen = decisions[p.source];
  if (chosen === "skip") return null;
  if (chosen) return chosen;
  return p.chosenId;
}

/**
 * Apply a step's decisions to the rows, in place of persisting a mapping.
 *
 * `column` is the file header feeding this step. Every row whose value resolves
 * gets that cell rewritten to the target's canonical name, so ExecBridge's own
 * exact-match resolution then succeeds.
 *
 * `dropUnmatched` decides what an unresolved value costs:
 *
 *   true  — for ACCOUNT and METRIC, which are identity. A row whose account or
 *           metric is unknown cannot become production data, and importing it
 *           only manufactures a rejected row.
 *   false — for CATEGORY, which is descriptive. The QC engine rates
 *           `CATEGORY_MATCHES_DEFINITION` as a *warn*, not a hard finding, so a
 *           category that does not match the rubric must NOT exclude the row.
 *           The original value passes through and QC flags it.
 *
 * Getting this wrong is not theoretical: a real file used short category names
 * ("Support") against a rubric using long ones ("Support & Risk Signals"), and
 * dropping on mismatch excluded all 127 rows.
 */
export function applyDecisions(
  rows: Record<string, string>[],
  column: string | null,
  proposals: MatchProposal[],
  decisions: Record<string, string>,
  candidates: Candidate[],
  dropUnmatched = true,
  /**
   * Which side of the candidate to write back.
   *
   * "name" for `account_name` and `category`, whose columns hold display text.
   * "id"   for `metric_id`, whose column holds an identifier — writing the
   *        display name there produced values like "Monthly Recurring Revenue
   *        (MRR)" in an id column, and ExecBridge rejected every such row.
   */
  writeField: "name" | "id" = "name"
): { rows: Record<string, string>[]; dropped: number } {
  if (!column || proposals.length === 0) return { rows, dropped: 0 };

  const valueById = new Map(
    candidates.map(c => [c.id, writeField === "id" ? c.id : c.name])
  );
  const targetBySource = new Map<string, string | null>();
  proposals.forEach(p => {
    const id = effectiveTarget(p, decisions);
    targetBySource.set(p.source, id ? (valueById.get(id) ?? null) : null);
  });

  const out: Record<string, string>[] = [];
  let dropped = 0;
  rows.forEach(r => {
    const source = (r[column] ?? "").trim();
    if (!source) {
      out.push(r);
      return;
    }
    if (!targetBySource.has(source)) {
      out.push(r);
      return;
    }
    const target = targetBySource.get(source);
    if (target == null) {
      if (dropUnmatched) {
        dropped++;
        return;
      }
      // Descriptive column: keep the row and its original value.
      out.push(r);
      return;
    }
    out.push({ ...r, [column]: target });
  });
  return { rows: out, dropped };
}

/** Column the resolved account UUID is stamped into before upload. */
export const ACCOUNT_ID_COLUMN = "__resolved_account_id";

/**
 * Stamp each row with the account UUID the Accounts step resolved.
 *
 * Without this the importer's de-duplication key falls back to
 * `source_account_id` (`_staging_natural_key`), and a file whose source id is a
 * constant placeholder — "SFDC-UNKNOWN" on every row, which is real — collapses
 * every account into one bucket. Six accounts sharing a metric and date then
 * look like five duplicates, and only one row survives.
 *
 * We already know the account by this point, so supplying the identity column
 * removes the guess entirely.
 */
export function stampAccountIds(
  rows: Record<string, string>[],
  column: string | null,
  proposals: MatchProposal[],
  decisions: Record<string, string>
): Record<string, string>[] {
  if (!column || proposals.length === 0) return rows;
  const idBySource = new Map<string, string | null>();
  proposals.forEach(p =>
    idBySource.set(p.source, effectiveTarget(p, decisions))
  );

  return rows.map(r => {
    const id = idBySource.get((r[column] ?? "").trim());
    return id ? { ...r, [ACCOUNT_ID_COLUMN]: id } : r;
  });
}
